/* @flow */
import { useIntl } from 'react-intl';

// Hooks
import useEvent from 'src/shared/domain/event/hook/useEvent';
import usePlatformURL from 'src/shared/domain/event/hook/usePlatformURL';
import useVirtualBraindate from 'plugin/virtual-braindate/hook/useVirtualBraindate';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';

// Utils
import messages from 'src/shared/domain/topic/l10n/topicL10n';
import { getEventName } from '@braindate/domain/lib/event/util';
import { trackGAEvent } from 'src/shared/core/service/tracking/gaService';
import { defaultBraindateSingular } from '@braindate/domain/lib/event/setting';
import {
  getTopicTitle,
  getTopicPermalink,
} from '@braindate/domain/lib/topic/util';
import {
  getSocialSharingHashtag,
  getSocialSharingTwitterHandle,
  isSocialSharingBraindateHashtagHidden,
} from 'plugin/social-sharing/util/socialSharingUtils';

// Types
import type { Topic } from '@braindate/domain/lib/topic/type';

const useShareToTwitter = (topic?: Topic) => {
  const event = useEvent();
  const intl = useIntl();

  const { braindateSingular } = useEventBraindateName();
  const isVirtualBraindateEnabled = useVirtualBraindate();
  const platformURL = usePlatformURL();

  const eventHashtag = getSocialSharingHashtag(event, intl.locale);
  const twitterHandle = getSocialSharingTwitterHandle(event, intl.locale);
  const hideBraindateHashtag = isSocialSharingBraindateHashtagHidden(event);

  const title = !!topic && getTopicTitle(topic, intl.locale);
  const eventName = getEventName(event, intl.locale);
  const topicPermalink = !!topic && getTopicPermalink(topic);

  const getText = () => {
    const params = {
      title,
      eventName: encodeURIComponent(eventName),
      eventHandle: twitterHandle,
      braindateSingular,
    };

    if (isVirtualBraindateEnabled) {
      return intl.formatMessage(
        twitterHandle
          ? messages.virtualShareText
          : messages.virtualShareTextWithoutHandle,
        params,
      );
    }

    return intl.formatMessage(
      twitterHandle ? messages.shareText : messages.shareTextWithoutHandle,
      params,
    );
  };

  const getHashtags = () => {
    if (
      braindateSingular === defaultBraindateSingular &&
      !hideBraindateHashtag
    ) {
      if (eventHashtag) return `${braindateSingular},${eventHashtag}`;
      return `${braindateSingular}`;
    }

    if (eventHashtag) {
      return `${eventHashtag}`;
    }
  };

  const shareToTwitter = () => {
    trackGAEvent('Topic details', 'Share on linkedIn');

    const text = getText();
    const url = topicPermalink
      ? `${topicPermalink}?utm_source=twitter`
      : `${platformURL}?utm_source=twitter`;

    const hashtags = getHashtags();
    const href = `https://twitter.com/intent/tweet?ref_src=twsrc%5Etfw&text=${text}&url=${url}${
      hashtags && hashtags.length ? `&hashtags=${hashtags}` : ''
    }`;

    if (typeof window !== 'undefined' && !!window.open) {
      window.open(href, 'newwindow', 'width=500,height=400');
    }
  };

  return shareToTwitter;
};

export default useShareToTwitter;
